// AP-167 · Fonte única da verdade para o status de um processo no Portal.
// Garante que Badge, Timeline, WorkflowTracker, Mensagem contextual e
// Histórico sempre leiam o MESMO status. Sem divergências do tipo
// "Status: Publicado / Workflow: Aprovado / Mensagem: aprovado".
//
// Regra: o workflow_stage é a fonte canônica (o backend auto-sincroniza
// workflow_stage sempre que o funcionário atualiza o status do processo
// em processes/{pid}). Quando ausente, caímos no `status` bruto.
//
// Importante: todos os textos vêm da função t() do PortalI18nContext —
// nunca retornar strings hardcoded em PT.

// Chaves canônicas do tracker (devem bater com WORKFLOW_STAGES).
export const PORTAL_STAGE_KEYS = [
  "cadastro",
  "enviado",
  "analise",
  "aprovado",
  "publicado",
  "finalizado",
];

// Mapeia qualquer rótulo/alias técnico (vindo de process_workflow_statuses) para
// uma das 6 etapas canônicas. Mantém o Portal sempre em uma linha visual limpa.
const ALIAS_TO_STAGE = {
  cadastro: "cadastro", open: "cadastro", novo: "enviado",
  enviado: "enviado", docs_received: "enviado",
  analise: "analise", in_review: "analise", in_progress: "analise",
  waiting: "analise", on_hold: "analise", awaiting_docs: "analise",
  aprovado: "aprovado", approved: "aprovado",
  publicado: "publicado", published: "publicado",
  finalizado: "finalizado", completed: "finalizado",
  cancelled: "finalizado", rejeitado: "finalizado", rejected: "finalizado",
};

const TONE = {
  cadastro:   { color: "sky",     dot: "bg-sky-500",     bg: "bg-sky-50 text-sky-700 border-sky-200",         card: "bg-sky-50 border-sky-200 text-sky-900",       icon: "✔" },
  enviado:    { color: "blue",    dot: "bg-blue-500",    bg: "bg-blue-50 text-blue-700 border-blue-200",      card: "bg-blue-50 border-blue-200 text-blue-900",    icon: "✔" },
  analise:    { color: "violet",  dot: "bg-violet-500",  bg: "bg-violet-50 text-violet-700 border-violet-200",card: "bg-violet-50 border-violet-200 text-violet-900", icon: "🔵" },
  aprovado:   { color: "emerald", dot: "bg-emerald-500", bg: "bg-emerald-50 text-emerald-700 border-emerald-200", card: "bg-emerald-50 border-emerald-200 text-emerald-900", icon: "✔" },
  publicado:  { color: "indigo",  dot: "bg-indigo-500",  bg: "bg-indigo-50 text-indigo-700 border-indigo-200", card: "bg-indigo-50 border-indigo-200 text-indigo-900", icon: "✔" },
  finalizado: { color: "emerald", dot: "bg-emerald-500", bg: "bg-emerald-50 text-emerald-700 border-emerald-200", card: "bg-emerald-50 border-emerald-200 text-emerald-900", icon: "✔" },
};

export function normalizeStage(raw) {
  const key = String(raw || "").toLowerCase().trim().replace(/\s+/g, "_");
  if (!key) return "cadastro";
  return ALIAS_TO_STAGE[key] || (PORTAL_STAGE_KEYS.includes(key) ? key : "cadastro");
}

/**
 * Resolve o status consolidado de um processo.
 * `proc` deve ser o payload retornado por /portal/processes/:pid.
 * `t` é a função de tradução do PortalI18nContext.
 *
 * Retorna sempre:
 *   {
 *     stageKey:   "cadastro" | "enviado" | ... | "finalizado"
 *     label:      <string traduzida — ex. "Apwouve">
 *     message:    <mensagem contextual traduzida>
 *     tone:       { color, dot, bg, card, icon }
 *   }
 */
export function resolveProcessStatus(proc, t) {
  // workflow_stage é a fonte canônica — ambos vêm do backend em sincronia.
  const stage = normalizeStage(proc?.workflow_stage || proc?.status);
  const tone = TONE[stage];
  const _label = t(`stage.${stage}.label`);
  const _msg   = t(`stage.${stage}.message`);
  return {
    stageKey: stage,
    label:   _label && _label !== `stage.${stage}.label`   ? _label : stage,
    message: _msg && _msg !== `stage.${stage}.message` ? _msg : "",
    tone,
  };
}
